import { FILE_LIST_LIMIT, fileIdsOf, isFileElement } from '../fileValues';
import { columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';
import { entriesOf } from '../repeatingGroupEntries';
import { isHiddenFromMe, isSeeOnlyElement } from '../elementAccess';
import { fileRefusalWords, precheckFile } from '../fileUpload';
import { isBlockedFileName } from '../../services/attachments';

/**
 * Bulk attach (KPR-187): what happens to each matched file, decided before a
 * byte moves — the batch rule "say what will change before it changes"
 * (platform-standards §3, Batch actions).
 *
 * Pure. For each file `{ key, file, itemId }` it answers one of
 *
 *   { action: 'upload' }
 *   { action: 'skip',   words }   already on that item: same name AND size
 *   { action: 'refuse', words }   would be refused; the reason, in words
 *   { action: 'none' }            no item chosen yet
 *
 * mirroring what the API would answer (the upload route's blocked types and
 * 100 MB, the file element's accept / maxSizeMb / 20-file list, a single
 * element holding one file). The server still has the last word; this only
 * keeps a doomed upload from spending the person's time and their share of
 * the upload budget.
 *
 * `destination` is null for the item's other attachments, or an element
 * NAME: items of several cards each resolve it on their own card
 * (`elementFor(cardId, name)`), and an item whose card has no such file
 * element is refused, never quietly sent to other attachments.
 *
 * An upload request's entry may name a repeating group's file COLUMN
 * (`targetFor(key)` answers `{ entryId, column }`, KPR-940; `destination` is
 * then the group's name). The entry must still be on the item, the column
 * visible and changeable and a file column, and the column's own rules
 * (accept, maxSizeMb, one file or a list of 20) bind, in the column's words.
 *
 * A single-file element that already holds a file is refused rather than
 * replaced (⚑ KPR-187): replacing deletes the old file, and a bulk pass is
 * the wrong place to do that by surprise. The item's own form replaces one.
 */

const labelOf = element => element?.label?.singular || element?.name || 'That element';

/**
 * The file column an upload request's entry names, on this item: `{ column,
 * entry }` or the sentence that refuses it.
 */
export function resolveColumn(item, group, groupName, { entryId, column: columnName }) {
	if (!group || !isRepeatingGroup(group)) return { words: `This item's card has no ${groupName} element.` };
	const entry = entriesOf(item.values?.[groupName]).find(e => e.id === entryId);
	if (!entry) return { words: `That entry is no longer on this item, so nothing can go in its ${columnName}.` };
	const column = columnsOf(group).find(c => c?.name === columnName);
	if (!column || isHiddenFromMe(column)) return { words: `${columnName} is not a column you can put files in.` };
	if (!isFileElement(column)) return { words: `${labelOf(column)} is not a file column.` };
	if (isSeeOnlyElement(column) || isSeeOnlyElement(group)) return { words: `${labelOf(column)} is see-only for you, so files cannot be put in it.` };
	return { column, entry };
}

/**
 * `files`: `[{ key, file, itemId }]` in the order shown.
 * `itemsById`: `{ [id]: { id, cardId, values } }`.
 * `existingFor(itemId)`: the item's attachments `[{ originalName, size }]`, or null when not known.
 */
export function planFiles({ files, itemsById, destination = null, destinationLabel = null, destinationFor = null, targetFor = null, elementFor, existingFor, attachAdmin = false }) {
	const out = {};
	// Per item, what this pass has already planned: names+sizes, and the count
	// headed for the destination element.
	const planned = new Map();
	const plannedFor = (itemId) => {
		if (!planned.has(itemId)) planned.set(itemId, { seen: new Set(), toElement: new Map() });
		return planned.get(itemId);
	};

	for (const { key, file, itemId } of files || []) {
		const item = itemId ? itemsById[itemId] : null;
		if (!item) { out[key] = { action: 'none' }; continue; }

		if (isBlockedFileName(file?.name)) {
			out[key] = { action: 'refuse', words: fileRefusalWords('file_blocked') };
			continue;
		}

		const fingerprint = `${file?.name}\u0000${file?.size}`;
		const mine = plannedFor(itemId);
		const existing = existingFor ? existingFor(itemId) : null;
		if ((existing || []).some(a => a?.originalName === file?.name && Number(a?.size) === Number(file?.size))) {
			out[key] = { action: 'skip', words: 'Already attached to this item: same name and size.' };
			continue;
		}
		if (mine.seen.has(fingerprint)) {
			out[key] = { action: 'skip', words: 'The same file is already chosen for this item.' };
			continue;
		}

		// One destination for the pass, or one per file (an upload request's
		// entries each name their own element, KPR-183).
		const dest = destinationFor ? destinationFor(key) : destination;
		let element = null;
		const target = dest && targetFor ? targetFor(key) : null;
		let slot = dest;
		let stored = 0;
		if (dest && target) {
			const resolved = resolveColumn(item, elementFor(item.cardId, dest), dest, target);
			if (resolved.words) { out[key] = { action: 'refuse', words: resolved.words }; continue; }
			element = resolved.column;
			stored = fileIdsOf(resolved.entry.elements?.[target.column]).length;
			slot = `${dest}\u0000${target.entryId}\u0000${target.column}`;
		} else if (dest) {
			element = elementFor(item.cardId, dest);
			if (!element || !isFileElement(element)) {
				out[key] = { action: 'refuse', words: `This item's card has no ${(!destinationFor && destinationLabel) || dest} element.` };
				continue;
			}
			stored = fileIdsOf(item.values?.[dest]).length;
		}
		const already = slot ? (mine.toElement.get(slot) || 0) : 0;
		if (element) {
			const multiple = element?.options?.allowMultiple === true;
			if (!multiple) {
				if (stored > 0) {
					out[key] = { action: 'refuse', words: `${labelOf(element)} already holds a file. Replace it from the item.` };
					continue;
				}
				if (already > 0) {
					out[key] = { action: 'refuse', words: `${labelOf(element)} holds one file, and another file is already chosen for this item.` };
					continue;
				}
			} else if (stored + already >= FILE_LIST_LIMIT) {
				out[key] = { action: 'refuse', words: fileRefusalWords('too_many_files', { element, limit: FILE_LIST_LIMIT }) };
				continue;
			}
		}

		const refusal = precheckFile(file, element, { count: element ? stored + already : 0, attachAdmin });
		if (refusal) {
			// Other attachments have no element to name: precheckFile's words
			// would say "This field".
			const words = !element && refusal.code === 'file_too_large' ? 'Attachments are limited to 100 MB each.' : refusal.words;
			out[key] = { action: 'refuse', words };
			continue;
		}

		mine.seen.add(fingerprint);
		if (element) mine.toElement.set(slot, already + 1);
		out[key] = { action: 'upload' };
	}
	return out;
}

/** Counts for the primary button and the summary line. */
export function summarise(plan) {
	const counts = { upload: 0, skip: 0, refuse: 0, none: 0 };
	for (const p of Object.values(plan || {})) counts[p.action] = (counts[p.action] || 0) + 1;
	return counts;
}
